import React from 'react'
import Image from 'next/image'

import { Column, Layout, Row, Rows, Section } from '@/components/layout'
import { Text } from '@/components/common'
import IconInfoBlock from '@/components/blocks/IconInfoBlock'
import TrackReceiptsHeader from '@/components/sections/TrackReceiptsHeader'
import PricingMain from '@/components/blocks/PricingMain'
import MobileAppFeatures from '@/components/blocks/MobileAppFeatures'
import TestimonialSliderSlim from '@/components/blocks/TestimonialSliderSlim'
import BoxedInfo from '@/components/blocks/BoxedInfo'
import CustomerSupport from '@/components/sections/CustomerSupport'
import FaqSection from '@/components/blocks/FaqSection'
import itemizationImg from '@/assets/track-receipts/itemization.png'
import trendsInsightsImg from '@/assets/track-receipts/trends-insights.png'
import fileTaxesImg from '@/assets/integrations/mapping-to-quickbooks.jpg'
import matchingReconciliationImg from '@/assets/transaction-streaming/matching-reconciliation.png'

import styles from './track-receipts.module.scss'

const pageId = 'trackReceipts'

const testimonials = [
  {
    text: 'It makes budgeting, tax preparation and documentation on my spending so easy. I have been a neat receipts user for over ten years and only once had a problem that the service dept. worked on quickly and efficiently.',
    author: 'Patricia M.',
  },
  {
    text: 'I am so thrilled learning more about NEAT. I\'ve used it for years and never knew it was such an Organizational BEAST! LOVE IT!',
    author: 'Douglas G.',
  },
  {
    text: 'Awesome to keep track of receipts.',
    author: 'Brian C.',
  },
  {
    text: 'Neat has cut my tax preparation from literally weeks, down to two days. Love it.',
    author: 'James F.',
  },
  {
    text: 'I have been using Neat for at least 7 years, and it\'s made tracking receipts and generating year end reports a breeze.',
    author: 'Joann D.',
  },
  {
    text: 'Makes end of the year filing so easy! No need to save receipts.',
    author: 'Michael K.',
  },
  {
    text: 'I meet with clients to do home closings every day. I tell them the benefits of using Neat.com. Most homeowners never think they may have a fire or flood. I have to remind them that paper or backup drives will be destroyed during both tragedies.',
    author: 'LaTonja A.',
  },
  {
    text: 'It pulls the information off the page, actually reads the text that\'s on there, and organizes it for you.',
    author: 'Josh Z.',
  },
  {
    text: 'The system is fantastic and eliminates many hours of filing and finding files.',
    author: 'Andy K.',
  },
]

const appFeatures = [
  {
    description: 'Receipt capture & import - turn your smartphone into an OCR scanning powerhouse',
  },
  {
    description: 'Access, organize & share receipts & docs with the mobile app\'s file management tools',
  },
  {
    description: 'Find receipts instantly with powerful search using keywords, dates, or categories',
  },
  {
    description: 'Manage documents, transactions, and expenses - reconcile by matching streamed transactions with uploaded receipts',
  },
  {
    description: 'Connect financial accounts and edit transactions with a tap to gain total control over your financial narrative',
  },
]

const neatUsers = [
  {
    title: 'Freelancers & Gig-workers',
    description: 'From freelancers, solopreneurs, consultants, e-commerce sellers and gig workers. They tend to have receipts that are scattered across emails, physical copies, phones, and cloud storage, making it difficult to find specific receipts when needed, especially during tax season or audits. Having a centralized system like Neat is a must-have to track and manage all financial documents.',
  },
  {
    title: 'Small Business Owners',
    description: 'From accountants, office managers, compliance officers and administrative staff. They tend to encounter slow scans, disorganized digital files, mounting compliance risks, rising storage costs, and software gaps are draining valuable time and disrupting daily workflows within small businesses. With Neat they are able to have a fast, OCR-powered automated document management system in the cloud.',
  },
  {
    title: 'Individuals/Personal',
    description: 'From individual to personal home fianances. They often face personal expense headaches. Trying to track everyday home and family expenses manually can be time consuming. Spreadsheets or apps that promise a way for expense management are often unreliable. Neat can provide a streamlined process to capture paper receipts and categorize expenses.',
  },
]

const faqs = ['imagesAccepted', 'whatIsOcr', 'capturedOnReceipt', 'howManyReceipts', 'receiptsIntoNeat', 'neatOnMobile', 'scannersForReceipts', 'smartCategorization', 'integrateWithSoftware', 'addUsersToAccount', 'supportAvailable']

export default function TrackReceipts() {
  return (
    <Layout.Content>
      <TrackReceiptsHeader
        subHeadline="Scan, store, and categorize receipts, bank statements, invoices and more instantly with Neat's powerful receipt tracker software. Simplify your budget and taxes with one simple app."
      />

      <Section className={styles.receiptManagement} paddingBottom={24}>
        <Rows gap={0}>
          <Row center textCenter>
            <h2 className="c-brand">Receipt management from anywhere</h2>
          </Row>
          <div className={styles.receiptManagementGrid}>
            <IconInfoBlock img="/track-receipts/icons/searchable.svg" title="Track Receipts Everywhere" wide>
              <p>Snap a picture using our mobile app, email, drag and drop documents into Neat, or import with a <a href="/receipt-document-scanners/">compatible scanner</a>. Then, add these items to expense reports for quick review/analysis or timely reimbursement.</p>
            </IconInfoBlock>

            <IconInfoBlock img="/track-receipts/icons/data-extraction.svg" title="Save Time On Data Entry" wide>
              <p>Our powerful OCR software can extract information from any paper document, allowing you to enter receipts quickly and accurately.</p>
            </IconInfoBlock>
            <IconInfoBlock img="/track-receipts/icons/always-organized.svg" title="Organize Files Your Way" wide>
              <p>Organize files by expense type, tax category, and more. Quickly find files with our keyword search and filtering capabilities.</p>
            </IconInfoBlock>

            <IconInfoBlock img="/track-receipts/icons/integrations.png" title="Connect With Integrations" wide>
              <p>Send your data to QuickBooks, TurboTax, H&amp;R Block, and more with a click of a button. <a href="/integrations/">See all integrations</a>.</p>
            </IconInfoBlock>
          </div>

        </Rows>
      </Section>

      <Section className={styles.aiSection}>
        <Rows gap={0}>
          <Row center textCenter>
            <Column className={styles.leftSection}>
              <h2 className="c-brand">AI-powered receipt management system at your fingertips</h2>
            </Column>
            <Column className={styles.rightSection}>
              <p>Eliminate the frustration of manual expense tracking. Our AI-powered app scans receipts and automatically extracts key information,
                creating accurate records instantly. Our patented technology handles the tedious work, so you don't have to.
              </p>
            </Column>
          </Row>
        </Rows>
      </Section>

      <MobileAppFeatures appFeatures={appFeatures} title="Track with our Mobile Receipt Scanner App" />

      <Section paddingTop={0}>
        <Rows gap={64}>
          <Row center>
            <h2 className="text-center">Enhance Your Experience with Automated Insights</h2>
          </Row>
          <Row>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={itemizationImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>Organize Data with Line Itemization</h3>
              <p>Breakdown your receipts, line-by-line. You can categorize, split, add, and edit line items for any matched receipt. Say goodbye to manually importing and managing multiple copies of the same receipt.</p>
            </Column>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={trendsInsightsImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>View Trends and Insights</h3>
              <p>Easily monitor and analyze your finances with live data about your cash balance, top expense categories, total sales, and more. All automated through your stream of transactions!</p>
            </Column>
          </Row>
          <Row>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={fileTaxesImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>File Your Taxes Quickly (And Accurately)</h3>
              <p>Easily send expenses to your tax or accounting software. Map expenses a single time to match your QuickBooks preferences, and incoming data will be automatically coded. Export tax categories, expenses, and document images to tax software like TurboTax to eliminate the need to type in data.</p>
            </Column>
            <Column alignCenter textCenter>
              <Image
                alt=""
                src={matchingReconciliationImg}
                style={{ maxHeight: '300px', width: 'auto' }}
              />
              <h3>Reconcile Receipts and Invoices</h3>
              <p>Match, categorize and reconcile with ease. Have confidence that all of your transactions are centralized and accurate. No need to reconcile off of a bank statement, every transaction source is accounted for in Neat.</p>
            </Column>
          </Row>
        </Rows>
      </Section>

      <BoxedInfo boxes={neatUsers} sectionTitle="Who uses Neat?" />

      <CustomerSupport
        bgImage="/track-receipts/track-receipts-banner.webp"
        description="Track every receipt and never miss a deduction again. Our receipt tracking app for small businesses integrates with tax software to keep your financial records audit-ready."
        title="Make Tax Season Stress-Free with Neat!"
      />

      <Section bgSecondary>
        <Text.H2 style={{ marginBottom: '30px' }}>Customers love Neat!</Text.H2>
        <Column wrap>
          <TestimonialSliderSlim fixedHeight="250" lightTheme multiSlides slides={testimonials} />
        </Column>
      </Section>

      <Section bgBrandTertiary style={{ backgroundColor: '#E9E8FF' }}>
        <PricingMain hasBottomContent />
      </Section>

      <FaqSection faqs={faqs} pageId={pageId} />
    </Layout.Content>
  )
}

TrackReceipts.layoutProps = {
  pageId,
}
